import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Users,
  Plus,
  Search,
  Loader2,
  Building,
  Mail,
  Phone,
  Globe,
  MapPin,
  Package,
  UserCheck,
  Edit,
  Trash2,
  X,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const COUNTRIES = [
  "China", "Japan", "South Korea", "Taiwan", "Hong Kong",
  "Singapore", "Malaysia", "Thailand", "Vietnam", "Indonesia", "Philippines",
  "India", "Bangladesh", "Pakistan", "Sri Lanka",
  "UAE", "Saudi Arabia", "Oman", "Qatar", "Bahrain", "Kuwait",
  "Germany", "Netherlands", "Belgium", "France", "United Kingdom", "Spain", "Italy", "Poland", "Greece", "Turkey",
  "United States", "Canada", "Mexico",
  "Brazil", "Argentina", "Chile", "Colombia", "Peru",
  "South Africa", "Egypt", "Morocco", "Nigeria", "Kenya",
  "Australia", "New Zealand"
].sort();

function AdminAgents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    company: '',
    phone: '',
    assigned_countries: []
  });

  useEffect(function() {
    loadData();
  }, []);

  async function loadData() {
    try {
      const agentsRes = await api.get('/admin/agents');
      setAgents(agentsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      company: '',
      phone: '',
      assigned_countries: []
    });
  }

  async function handleCreateAgent(e) {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.first_name || !formData.last_name || !formData.company) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.assigned_countries.length === 0) {
      toast.error('Please assign at least one country');
      return;
    }

    setCreating(true);
    try {
      // Use assigned_countries as assigned_regions (backend treats them the same)
      await api.post('/admin/agents', {
        ...formData,
        assigned_regions: formData.assigned_countries
      });
      toast.success('Agent created successfully');
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create agent');
    } finally {
      setCreating(false);
    }
  }

  async function handleDeactivateAgent(agentId) {
    if (!window.confirm('Are you sure you want to deactivate this agent?')) return;

    try {
      await api.delete('/admin/agents/' + agentId);
      toast.success('Agent deactivated');
      loadData();
    } catch (err) {
      toast.error('Failed to deactivate agent');
    }
  }

  function toggleCountry(country) {
    setFormData(function(prev) {
      const current = prev.assigned_countries;
      if (current.includes(country)) {
        return { ...prev, assigned_countries: current.filter(function(c) { return c !== country; }) };
      } else {
        return { ...prev, assigned_countries: [...current, country] };
      }
    });
  }

  const filteredAgents = agents.filter(function(agent) {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return agent.email.toLowerCase().includes(term) ||
           agent.first_name.toLowerCase().includes(term) ||
           agent.last_name.toLowerCase().includes(term) ||
           agent.company?.toLowerCase().includes(term);
  });

  // Get unique countries from all agents
  const coveredCountries = new Set();
  agents.forEach(function(agent) {
    (agent.assigned_regions || []).forEach(function(r) { coveredCountries.add(r); });
  });

  return (
    <div className="space-y-6" data-testid="admin-agents">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-[32px] sv-display text-ink">Agents</h1>
          <p className="text-gray-600 mt-1">Create and manage shipping agents by country</p>
        </div>
        <Button 
          className="bg-ink hover:bg-ink-800 rounded-full"
          onClick={function() { setShowCreateModal(true); }}
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Agent
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-sm shadow-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-sm flex items-center justify-center">
              <Users className="h-5 w-5 text-ink" />
            </div>
            <div>
              <p className="text-2xl font-normal tracking-[-0.01em] text-ink">{agents.length}</p>
              <p className="text-xs text-gray-500">Total agents</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-sm shadow-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-sm flex items-center justify-center">
              <UserCheck className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-normal tracking-[-0.01em] text-ink">
                {agents.filter(function(a) { return a.status === 'approved' && (a.assigned_regions || []).length > 0; }).length}
              </p>
              <p className="text-xs text-gray-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-sm shadow-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
              <Globe className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-normal tracking-[-0.01em] text-ink">{coveredCountries.size}</p>
              <p className="text-xs text-gray-500">Countries covered</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-sm shadow-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-sm flex items-center justify-center">
              <Package className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-normal tracking-[-0.01em] text-ink">
                {agents.reduce(function(sum, a) { return sum + (a.customer_count || 0); }, 0)}
              </p>
              <p className="text-xs text-gray-500">Total customers</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-sm shadow-card p-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search agents by name, email, or company..."
            className="pl-12 h-12 rounded-sm"
            value={searchTerm}
            onChange={function(e) { setSearchTerm(e.target.value); }}
          />
        </div>
      </div>

      {/* Agents List */}
      <div className="bg-white rounded-sm shadow-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : filteredAgents.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {filteredAgents.map(function(agent) {
              const hasCountries = (agent.assigned_regions || []).length > 0;
              return (
                <div key={agent.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-ink rounded-sm flex items-center justify-center text-white font-medium text-lg">
                        {agent.first_name[0]}{agent.last_name[0]}
                      </div>
                      <div>
                        <h3 className="font-medium text-ink text-lg">
                          {agent.first_name} {agent.last_name}
                        </h3>
                        <div className="flex items-center gap-2 text-gray-500 text-sm mt-1">
                          <Building className="h-4 w-4" />
                          <span>{agent.company || 'No company'}</span>
                        </div>
                        <div className="flex items-center gap-4 text-gray-500 text-sm mt-1">
                          <div className="flex items-center gap-1">
                            <Mail className="h-4 w-4" />
                            <span>{agent.email}</span>
                          </div>
                          {agent.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="h-4 w-4" />
                              <span>{agent.phone}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {hasCountries ? (
                            (agent.assigned_regions || []).map(function(country) {
                              return (
                                <Badge key={country} variant="outline" className="bg-blue-500/5 text-blue-600 border-blue-500/20">
                                  <MapPin className="h-3 w-3 mr-1" />
                                  {country}
                                </Badge>
                              );
                            })
                          ) : (
                            <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200">
                              No countries assigned
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={hasCountries && agent.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {hasCountries && agent.status === 'approved' ? 'Active' : 'Inactive'}
                      </Badge>
                      <div className="flex items-center gap-4 mt-4 text-sm">
                        <div className="text-center">
                          <p className="font-medium text-ink">{agent.customer_count || 0}</p>
                          <p className="text-gray-500 text-xs">Customers</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium text-ink">{agent.booking_count || 0}</p>
                          <p className="text-gray-500 text-xs">Bookings</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="rounded-full text-red-500 hover:bg-red-50"
                          onClick={function() { handleDeactivateAgent(agent.id); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No agents found</p>
            <p className="text-sm text-gray-400 mt-1">Create your first agent to get started</p>
          </div>
        )}
      </div>

      {/* Create Agent Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-medium tracking-[-0.01em] text-ink">Create new agent</h2>
              <Button 
                variant="ghost" 
                size="sm" 
                className="rounded-full"
                onClick={function() { setShowCreateModal(false); resetForm(); }}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <form onSubmit={handleCreateAgent} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-medium">First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={function(e) { setFormData({...formData, first_name: e.target.value}); }}
                    className="h-11 rounded-sm"
                    placeholder="John"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={function(e) { setFormData({...formData, last_name: e.target.value}); }}
                    className="h-11 rounded-sm"
                    placeholder="Smith"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Agency/Company Name *</Label>
                <Input
                  value={formData.company}
                  onChange={function(e) { setFormData({...formData, company: e.target.value}); }}
                  className="h-11 rounded-sm"
                  placeholder="Global Freight Services"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={function(e) { setFormData({...formData, email: e.target.value}); }}
                  className="h-11 rounded-sm"
                  placeholder="agent@company.com"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={function(e) { setFormData({...formData, password: e.target.value}); }}
                  className="h-11 rounded-sm"
                  placeholder="Minimum 6 characters"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={function(e) { setFormData({...formData, phone: e.target.value}); }}
                  className="h-11 rounded-sm"
                  placeholder="+1 234 567 8900"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Assigned Countries *</Label>
                <p className="text-xs text-gray-500 mb-2">Select countries this agent will cover. Users from these countries will be assigned to this agent.</p>
                <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-3 bg-gray-50 rounded-sm">
                  {COUNTRIES.map(function(country) {
                    const isSelected = formData.assigned_countries.includes(country);
                    const isCovered = coveredCountries.has(country);
                    return (
                      <button
                        key={country}
                        type="button"
                        onClick={function() { toggleCountry(country); }}
                        className={"px-3 py-1.5 rounded-full text-sm font-medium transition-all " + (
                          isSelected 
                            ? 'bg-blue-500 text-white' 
                            : isCovered
                              ? 'bg-gray-200 text-gray-500 border border-gray-300'
                              : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-500'
                        )}
                        title={isCovered && !isSelected ? 'Already assigned to another agent' : ''}
                      >
                        {isSelected && <Check className="h-3 w-3 inline mr-1" />}
                        {country}
                      </button>
                    );
                  })}
                </div>
                {formData.assigned_countries.length > 0 && (
                  <p className="text-xs text-blue-600 mt-2">
                    {formData.assigned_countries.length} country(s) selected
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button"
                  variant="outline" 
                  className="flex-1 h-12 rounded-full"
                  onClick={function() { setShowCreateModal(false); resetForm(); }}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  className="flex-1 h-12 bg-ink hover:bg-ink-800 rounded-full"
                  disabled={creating}
                >
                  {creating ? (
                    <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Creating...</>
                  ) : (
                    'Create Agent'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAgents;
